import { useCallback, useMemo, useState } from 'react';

const defaultGetId = item => item?._id;

export const useSelect = (items = [], options = {}) => {
  const getId = options.getId || defaultGetId;
  const [selectedIds, setSelectedIds] = useState([]);

  const visibleIds = useMemo(() => (Array.isArray(items) ? items.map(getId).filter(Boolean) : []), [items, getId]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedVisibleCount = useMemo(() => visibleIds.filter(id => selectedIdSet.has(id)).length, [
    visibleIds,
    selectedIdSet
  ]);

  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const isSelected = useCallback(id => selectedIdSet.has(id), [selectedIdSet]);

  const toggle = useCallback(id => {
    if (!id) return;
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]));
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(prev => {
      const prevSet = new Set(prev);
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prevSet.has(id));

      if (allSelected) {
        return prev.filter(id => !visibleIds.includes(id));
      }

      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return [...next];
    });
  }, [visibleIds]);

  const clear = useCallback(() => setSelectedIds([]), []);

  const deselect = useCallback(id => {
    setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
  }, []);

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    hasSelection: selectedIds.length > 0,
    isSelected,
    allVisibleSelected,
    someVisibleSelected,
    toggle,
    toggleAll,
    clear,
    deselect,
    setSelectedIds
  };
};
